<template>
  <div class="h-full overflow-y-scroll bg-background-light">
    <div class="p-4 sm:p-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 class="text-2xl font-bold">Leads</h3>
          <p class="text-sm text-gray-500 mt-1">Contactos recibidos desde la página de PickPack.</p>
        </div>
        <div class="inline-flex self-start border border-gray-300 rounded overflow-hidden bg-white">
          <button
            v-for="option in filters"
            :key="option.value"
            type="button"
            class="px-3 py-2 text-sm"
            :class="filter === option.value ? 'bg-primary text-white' : 'text-gray-700'"
            @click="filter = option.value"
          >
            {{ option.label }} <span class="ml-1 opacity-70">{{ filterCount(option.value) }}</span>
          </button>
        </div>
      </div>

      <div v-if="hasLoaded && filteredLeads.length === 0" class="mt-6 border border-gray-200 rounded bg-white p-8 text-center text-gray-500">
        No hay leads en esta sección.
      </div>

      <div v-if="filteredLeads.length > 0" class="sm:hidden mt-5 space-y-3">
        <article v-for="lead in filteredLeads" :key="lead.id" class="border border-gray-200 rounded bg-white p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h4 class="font-bold">{{ lead.business_name }}</h4>
              <p class="text-sm text-gray-600 mt-1">{{ lead.name }}</p>
            </div>
            <span class="rounded-full px-2 py-1 text-xs font-medium" :class="lead.contacted ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'">
              {{ lead.contacted ? "Contactado" : "Nuevo" }}
            </span>
          </div>
          <p class="text-xs text-gray-500 mt-3">{{ formatDate(lead.created_at) }}</p>
          <div class="grid grid-cols-2 gap-2 mt-4">
            <a :href="whatsappLink(lead)" target="_blank" rel="noopener noreferrer" class="rounded bg-primary text-white text-center font-bold py-2 px-3 text-sm">WhatsApp</a>
            <a v-if="lead.business_link" :href="lead.business_link" target="_blank" rel="noopener noreferrer" class="rounded border border-primary text-primary text-center font-bold py-2 px-3 text-sm">Ver negocio</a>
          </div>
          <button type="button" class="w-full mt-2 rounded border border-gray-300 py-2 px-3 text-sm font-medium disabled:opacity-50" :disabled="updatingId === lead.id" @click="toggleContacted(lead)">
            {{ lead.contacted ? "Marcar como nuevo" : "Marcar como contactado" }}
          </button>
        </article>
      </div>

      <div v-if="filteredLeads.length > 0" class="hidden sm:block mt-5 border border-gray-200 rounded bg-white overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="border-b border-gray-200 text-gray-500">
              <tr>
                <th class="py-3 px-4 font-medium">Recibido</th>
                <th class="py-3 px-4 font-medium">Negocio</th>
                <th class="py-3 px-4 font-medium">Contacto</th>
                <th class="py-3 px-4 font-medium">Enlace</th>
                <th class="py-3 px-4 font-medium">Estado</th>
                <th class="py-3 px-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="lead in filteredLeads" :key="lead.id" class="border-b border-gray-100 last:border-b-0">
                <td class="py-4 px-4 whitespace-nowrap text-gray-500">{{ formatDate(lead.created_at) }}</td>
                <td class="py-4 px-4 font-bold">{{ lead.business_name }}</td>
                <td class="py-4 px-4">
                  <p>{{ lead.name }}</p>
                  <a :href="whatsappLink(lead)" target="_blank" rel="noopener noreferrer" class="text-primary underline whitespace-nowrap">
                    {{ lead.whatsapp_country_code }} {{ lead.whatsapp_number }}
                  </a>
                </td>
                <td class="py-4 px-4">
                  <a v-if="lead.business_link" :href="lead.business_link" target="_blank" rel="noopener noreferrer" class="text-primary underline">Abrir</a>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="py-4 px-4">
                  <span class="inline-flex rounded-full px-3 py-1 text-xs font-medium" :class="lead.contacted ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'">
                    {{ lead.contacted ? "Contactado" : "Nuevo" }}
                  </span>
                </td>
                <td class="py-4 px-4 text-right">
                  <button type="button" class="rounded border border-primary text-primary font-bold py-2 px-3 whitespace-nowrap disabled:opacity-50" :disabled="updatingId === lead.id" @click="toggleContacted(lead)">
                    {{ lead.contacted ? "Marcar nuevo" : "Marcar contactado" }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { store } from "../store.js";
import { utils } from "./utils/utils.js";

export default {
  name: "AdminLeads",
  setup() {
    const leads = ref([]);
    const filter = ref("all");
    const hasLoaded = ref(false);
    const updatingId = ref(null);
    const filters = [
      { label: "Todos", value: "all" },
      { label: "Nuevos", value: "new" },
      { label: "Contactados", value: "contacted" },
    ];

    const filteredLeads = computed(() => {
      if (filter.value === "new") return leads.value.filter((lead) => !lead.contacted);
      if (filter.value === "contacted") return leads.value.filter((lead) => lead.contacted);
      return leads.value;
    });

    async function load() {
      try {
        leads.value = await utils.getLeads();
        hasLoaded.value = true;
      } catch (error) {
        if (error.status !== 401) {
          useToast().error("No se pudieron cargar los leads", { position: "top-right" });
        }
      }
    }

    async function toggleContacted(lead) {
      updatingId.value = lead.id;
      try {
        const updated = await utils.updateLeadContacted(lead.id, !lead.contacted);
        const index = leads.value.findIndex((item) => item.id === lead.id);
        if (index !== -1) leads.value[index] = updated;
      } catch {
        useToast().error("No se pudo actualizar el lead", { position: "top-right" });
      } finally {
        updatingId.value = null;
      }
    }

    function filterCount(value) {
      if (value === "new") return leads.value.filter((lead) => !lead.contacted).length;
      if (value === "contacted") return leads.value.filter((lead) => lead.contacted).length;
      return leads.value.length;
    }

    function whatsappLink(lead) {
      const code = String(lead.whatsapp_country_code || "").replace(/\D/g, "");
      const phone = String(lead.whatsapp_number || "").replace(/\D/g, "");
      return `https://wa.me/${code}${phone}`;
    }

    function formatDate(value) {
      return new Date(value).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "America/Mexico_City",
      });
    }

    onMounted(() => { if (store.authAdmin) load(); });
    watch(() => store.authAdmin, (authorized) => { if (authorized) load(); });

    return { filter, filteredLeads, filters, hasLoaded, updatingId, filterCount, formatDate, toggleContacted, whatsappLink };
  },
};
</script>
